import { CSRFTokenField } from "@circle-react-uikit/CSRFTokenField";
import { Modal } from "@circleco/compass/components/Modal";
import { TextArea } from "@circleco/compass/components/TextArea";
import { map } from "lodash";
import type { ComponentPropsWithoutRef, RefObject } from "react";
import { useMemo } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import { t } from "@/i18n-js/instance";

const REPORT_FORM_ID = "error-report-form";
const REPORT_HIDDEN_FIELD_KEYS = [
  "errorMessage",
  "errorStack",
  "componentStack",
] as const;

interface ErrorReportModalV4Props {
  errorCode: number;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit: NonNullable<ComponentPropsWithoutRef<"form">["onSubmit"]>;
  passThroughProps: Record<string, unknown>;
  recaptchaRef: RefObject<ReCAPTCHA | null>;
}

const toHiddenInputValue = (value: unknown): string | null => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return null;
};

export const ErrorReportModalV4 = ({
  errorCode,
  isOpen,
  onOpenChange,
  onSubmit,
  passThroughProps,
  recaptchaRef,
}: ErrorReportModalV4Props) => {
  const errorUrl = useMemo(() => window.location.href, []);
  const hiddenPassThroughProps = useMemo(
    () =>
      Object.fromEntries(
        REPORT_HIDDEN_FIELD_KEYS.flatMap(key => {
          const inputValue = toHiddenInputValue(passThroughProps[key]);
          return inputValue === null ? [] : [[key, inputValue]];
        }),
      ),
    [passThroughProps],
  );

  return (
    <Modal.Root open={isOpen} onOpenChange={onOpenChange} size="md">
      <Modal.Content>
        <Modal.Header
          title={t("error_page.v4.report_modal.title")}
          description={t("error_page.v4.report_modal.description")}
        />
        <Modal.Body>
          <form
            id={REPORT_FORM_ID}
            action="/error_reports/send_email_notification"
            acceptCharset="UTF-8"
            className="pt-1"
            method="post"
            onSubmit={onSubmit}
          >
            <CSRFTokenField />
            <label className="sr-only" htmlFor="error_detail">
              {t("error_page.v4.report_modal.placeholder")}
            </label>
            <TextArea
              id="error_detail"
              name="error_detail"
              placeholder={t("error_page.v4.report_modal.placeholder")}
              required
              rows={5}
            />
            <input
              value={errorCode}
              type="hidden"
              name="error_status"
              id="error_status"
            />
            <input
              value={errorUrl}
              type="hidden"
              name="error_url"
              id="error_url"
            />
            {map(hiddenPassThroughProps, (value, key) => (
              <input key={key} name={key} type="hidden" value={value} />
            ))}
            {window.CAPTCHA_SITE_KEY && (
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={window.CAPTCHA_SITE_KEY}
                size="invisible"
              />
            )}
          </form>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("cancel"),
            onClick: () => onOpenChange(false),
          }}
          primaryAction={{
            label: t("submit"),
            type: "submit",
            form: REPORT_FORM_ID,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
